import React, { useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { ShieldCheck, X, Clock3 } from "lucide-react";
import { api } from "@/lib/api";
import { markCopyrightConfirmed } from "@/lib/device";
import { toast } from "sonner";

/**
 * Consent modal triggered when the user clicks "Analyze".
 *
 * Three modes:
 *   - `firstTime={true}` → shows Terms + Privacy + Copyright checkboxes
 *                          (Terms & Privacy are one-off; stored on backend).
 *   - `firstTime={false}` + `quick={false}` → shows ONLY the per-upload
 *                          Copyright checkbox (asked on each upload).
 *   - `quick={true}` → one-click re-confirmation shown when the user
 *                     confirmed copyright in the last 5 minutes.
 */
export default function ConsentModal({ firstTime, quick, onAccepted, onClose }) {
  const { t } = useTranslation();
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [copyright, setCopyright] = useState(false);
  const [saving, setSaving] = useState(false);

  const canSubmit = quick ? true : firstTime ? terms && privacy && copyright : copyright;

  const submit = async () => {
    if (!canSubmit || saving) return;
    setSaving(true);
    try {
      if (firstTime) {
        await api.post("/consent", {
          terms_accepted: true,
          privacy_accepted: true,
          copyright_confirmed: true,
        });
      }
      // Always refresh the local copyright timestamp on success so the next
      // upload within 5 minutes uses the quick flow.
      markCopyrightConfirmed();
      onAccepted && onAccepted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("consent.error"));
    } finally {
      setSaving(false);
    }
  };

  const title = quick
    ? t("consent.quick_title")
    : firstTime
      ? t("consent.title")
      : t("consent.per_upload_title");
  const intro = quick
    ? t("consent.quick_intro")
    : firstTime
      ? t("consent.intro")
      : t("consent.per_upload_intro");
  const acceptLabel = quick
    ? t("consent.quick_accept")
    : firstTime
      ? t("consent.accept")
      : t("consent.confirm_upload");

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="consent-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="card-elev max-w-lg w-full p-6 sm:p-8 flex flex-col gap-4 max-h-[90vh] overflow-y-auto relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-md hover:bg-white/5"
          aria-label={t("consent.close")}
          data-testid="consent-close-btn"
        >
          <X size={16} />
        </button>

        <div className="flex items-center gap-3 pr-8">
          <div className="w-10 h-10 rounded-full bg-blue-500/15 flex items-center justify-center shrink-0">
            {quick ? (
              <Clock3 size={20} className="text-[color:var(--brand)]" />
            ) : (
              <ShieldCheck size={20} className="text-[color:var(--brand)]" />
            )}
          </div>
          <h2 className="font-display text-2xl font-semibold text-white">{title}</h2>
        </div>

        <p className="text-sm text-[color:var(--fg-secondary)] leading-relaxed">{intro}</p>

        {firstTime && (
          <>
            <div className="flex flex-col gap-1.5">
              <label className="flex items-start gap-3 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={terms}
                  onChange={(e) => setTerms(e.target.checked)}
                  className="mt-1 accent-[color:var(--brand)]"
                  data-testid="consent-terms-cb"
                />
                <span className="text-[color:var(--fg-secondary)]">
                  {t("consent.terms_prefix")} {t("consent.terms_link")}.
                </span>
              </label>
              <a
                href="/legal/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[color:var(--brand)] underline pl-6 hover:no-underline w-fit"
                data-testid="consent-terms-link"
              >
                {t("consent.read_full")} →
              </a>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="flex items-start gap-3 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={privacy}
                  onChange={(e) => setPrivacy(e.target.checked)}
                  className="mt-1 accent-[color:var(--brand)]"
                  data-testid="consent-privacy-cb"
                />
                <span className="text-[color:var(--fg-secondary)]">
                  {t("consent.privacy_prefix")} {t("consent.privacy_link")}.
                </span>
              </label>
              <a
                href="/legal/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[color:var(--brand)] underline pl-6 hover:no-underline w-fit"
                data-testid="consent-privacy-link"
              >
                {t("consent.read_full")} →
              </a>
            </div>
          </>
        )}

        {!quick && (
          <label className="flex items-start gap-3 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={copyright}
              onChange={(e) => setCopyright(e.target.checked)}
              className="mt-1 accent-[color:var(--brand)]"
              data-testid="consent-copyright-cb"
            />
            <span className="text-[color:var(--fg-secondary)]">
              {t("consent.copyright")}
            </span>
          </label>
        )}

        {/* Option-C reminder · appears ONLY on the short per-upload modal
            (2nd upload onward, plus every A/B comparison). Reinforces that
            Terms + Privacy previously accepted still apply, without asking
            the user to re-tick them every time. */}
        {!firstTime && !quick && (
          <p
            className="text-xs text-[color:var(--fg-muted)] leading-snug pl-6"
            data-testid="consent-legal-reminder"
          >
            <Trans
              i18nKey="consent.per_upload_legal_reminder"
              components={{
                tos: (
                  <a
                    href="/legal/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-white transition-colors"
                  />
                ),
                privacy: (
                  <a
                    href="/legal/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-white transition-colors"
                  />
                ),
              }}
            />
          </p>
        )}

        <div className="flex flex-col sm:flex-row gap-2 items-stretch mt-2">
          <button
            className="btn-ghost flex-1 sm:flex-none"
            onClick={onClose}
            data-testid="consent-cancel-btn"
          >
            {t("consent.cancel")}
          </button>
          <button
            className="btn-primary flex-1 disabled:opacity-40"
            disabled={!canSubmit || saving}
            onClick={submit}
            data-testid="consent-accept-btn"
          >
            {saving ? t("consent.saving") : acceptLabel}
          </button>
        </div>

        {firstTime && (
          <p className="text-xs text-[color:var(--fg-muted)] mt-1">
            {t("consent.footer")}
          </p>
        )}
        {quick && (
          <p className="text-xs text-[color:var(--fg-muted)] mt-1">
            {t("consent.quick_footer")}
          </p>
        )}
      </div>
    </div>
  );
}
